/* eslint-disable */
// NotificationCard — one card. Split out of NotificationHost to keep both files
// well under the 300-line cap (Conv #6 / #163).
//
// ⚠️ TWO HARD RULES, BOTH SECURITY-SHAPED:
//
//   1. title, body and every string in payload render as TEXT.
//      dangerouslySetInnerHTML is FORBIDDEN here, without exception. This
//      component is mounted globally above every screen, so one unescaped field
//      is a stored XSS that reaches every session in the company. React's
//      default {value} interpolation is what you want, and there is no case in
//      this card where it is not.
//
//   2. dir="auto" on title, body and the note panel. People write Hebrew and
//      English into the same field; without it Hebrew renders left-aligned and
//      looks broken.

import React, { useState } from 'react';
import CollectionMovedChips, { COLLECTION_MOVED } from './CollectionMovedChips';
import NotificationAttachments from './NotificationAttachments';
import styles from './notifications.module.css';
import { canOpenNotification, openNotification } from '../../utils/entityRoutes';

function initialsOf(name) {
  if (!name) return 'S';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'S';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

// ⚠️ THE PAYLOAD-IMAGE READ PATH WAS REMOVED HERE (P6 §2.4). This file used to
// render payload.image_url / thumbnail_url / image directly. NOTHING has ever
// written those keys for a notification — the only writers in the tree are sell
// sheets and collections, and P3's composer never passed p_payload at all — so
// no shipped notification changes appearance. But a reader for a forbidden key
// is a live hole: the notification row is shared by every recipient, so a URL
// placed there would be resolved once by the sender and then belong to everyone
// who reaches the row, including the people the access filter excluded.
// Attachments replace it, and they carry {kind,id} only; each reader resolves
// the picture themselves under their own RLS.

// `preview` renders the card exactly as a recipient will see it, but INERT: no
// close, no Later, no Open, and no path to markRead. The composer uses this
// rather than a second card of its own — two definitions of one card drift, and
// then the preview is quietly lying about what will be sent.
export function NotificationCard({ row, senderName, onDismiss, preview = false }) {
  const [leaving, setLeaving] = useState(false);
  const n = row.notifications;
  const severity = n.severity === 'important' || n.severity === 'critical' ? n.severity : '';

  const eyebrow = n.notification_types?.label_en || n.type_key;
  const subtitle = typeof n.payload?.subtitle === 'string' ? n.payload.subtitle : '';
  const who = senderName || 'System';

  // Animate out, then hand back to the host. 260ms matches .leaving in the CSS.
  const dismiss = () => {
    if (preview || leaving) return;   // preview can never mark anything read
    setLeaving(true);
    setTimeout(() => onDismiss(row.id), 260);
  };

  return (
    <div
      className={`${styles.card}${severity ? ' ' + styles[severity] : ''}${leaving ? ' ' + styles.leaving : ''}`}
      role="status"
      data-test="notification-card"
      data-severity={n.severity}
    >
      <div className={styles.top}>
        <span className={styles.dot} />
        <span className={styles.eyebrow}>{eyebrow}</span>
        <span className={styles.time}>{relativeTime(n.created_at)}</span>
        {!preview && (
          <button
            type="button"
            className={styles.close}
            onClick={dismiss}
            aria-label="Dismiss notification"
            data-test="notification-close"
          >
            ×
          </button>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.bodyText}>
          <div className={styles.title} dir="auto" data-test="notification-title">{n.title}</div>
          {subtitle && <div className={styles.subtitle} dir="auto">{subtitle}</div>}
        </div>
      </div>

      {n.body && (
        <div className={styles.note} dir="auto" data-test="notification-body">{n.body}</div>
      )}

      {/* §2.3 precedence: attachments win outright; with none, this renders
          nothing and the card behaves exactly as it did before P6. */}
      {/* Navigating away dismisses the toast — leaving it floating over the
          record the reader just asked to see is its own small defect. `preview`
          is the composer's inert copy, which must never mark anything read. */}
      <div className={styles.attachWrap}>
        <NotificationAttachments payload={n.payload} entityType={n.entity_type} entityId={n.entity_id} onNavigate={preview ? undefined : dismiss} />
        {n.type_key === COLLECTION_MOVED ? <CollectionMovedChips n={n} /> : null}
      </div>

      <div className={styles.foot}>
        <span className={styles.avatar} aria-hidden="true">{initialsOf(who)}</span>
        <span className={styles.sender}>{who}</span>
        {!preview && <div className={styles.footBtns}>
          <button
            type="button"
            className={styles.textBtn}
            onClick={dismiss}
            data-test="notification-later"
          >
            Later
          </button>
          {/* P2b: Open now opens, via the shared entity-route map. It is absent
              entirely when the entity cannot be reached — a dead Open teaches
              people the system is broken. Navigating stamps read_at. */}
          {canOpenNotification(n) && (
            <button
              type="button"
              className={`${styles.textBtn} ${styles.primary}`}
              onClick={() => { if (openNotification(n)) dismiss(); }}
              data-test="notification-open"
            >
              Open
            </button>
          )}
        </div>}
      </div>
    </div>
  );
}

export default NotificationCard;
